import { loadRnnoise, RnnoiseWorkletNode } from "@sapphi-red/web-noise-suppressor";
import rnnoiseWorkletUrl from "@sapphi-red/web-noise-suppressor/rnnoiseWorklet.js?url";
import rnnoiseWasmUrl from "@sapphi-red/web-noise-suppressor/rnnoise.wasm?url";
import rnnoiseSimdWasmUrl from "@sapphi-red/web-noise-suppressor/rnnoise_simd.wasm?url";

/**
 * Pipes a local audio MediaStreamTrack through an RNNoise AudioWorklet
 * that strips background hum, keyboard tapping, fan noise, etc. before
 * the WebRTC encoder sees the bytes. Mirrors the Microsoft Teams
 * "Noise suppression: High" toggle.
 *
 * Pipeline:
 *   1. AudioContext at 48 kHz (RNNoise's only supported rate).
 *   2. MediaStreamAudioSourceNode wraps the raw mic.
 *   3. RnnoiseWorkletNode runs the WASM filter per 480-sample frame.
 *   4. MediaStreamAudioDestinationNode emits the processed stream.
 *   5. Caller swaps the outgoing track on every peer connection.
 *
 * Lifecycle: instantiate → await start() → grab `outputTrack` →
 * eventually call stop(). Idempotent stop().
 */
export class NoiseSuppressor {
    private readonly source: MediaStreamTrack;
    private ctx: AudioContext | null = null;
    private node: RnnoiseWorkletNode | null = null;
    private dest: MediaStreamAudioDestinationNode | null = null;
    private stopped = false;

    constructor(source: MediaStreamTrack) {
        this.source = source;
    }

    async start(): Promise<MediaStreamTrack> {
        // RNNoise is hard-coded to 48 kHz; passing a different rate
        // makes the filter sound chipmunked.
        this.ctx = new AudioContext({ sampleRate: 48_000 });

        // Both the worklet *and* the WASM binary need to be reachable
        // by URL. The worklet processor module gets registered on the
        // AudioWorklet scope; the WASM blob is fed to the constructor
        // as an ArrayBuffer (preferred over having the worklet fetch
        // it itself — the worklet scope can't see relative paths).
        await this.ctx.audioWorklet.addModule(rnnoiseWorkletUrl);
        const wasmBinary = await loadRnnoise({
            url: rnnoiseWasmUrl,
            simdUrl: rnnoiseSimdWasmUrl
        });

        // Wrap the raw mic track in a MediaStream so we can build the
        // source node from a stable MediaStream reference.
        const rawStream = new MediaStream([this.source]);
        const sourceNode = this.ctx.createMediaStreamSource(rawStream);

        this.node = new RnnoiseWorkletNode(this.ctx, {
            // We never expect more than mono mic input but RNNoise
            // can handle stereo if asked; cap conservatively.
            maxChannels: 1,
            wasmBinary
        });

        this.dest = this.ctx.createMediaStreamDestination();
        sourceNode.connect(this.node).connect(this.dest);

        const track = this.dest.stream.getAudioTracks()[0];
        if (!track) throw new Error("RNNoise destination produced no audio track");
        return track;
    }

    stop(): void {
        if (this.stopped) return;
        this.stopped = true;
        try { this.node?.destroy(); } catch { /* ignore */ }
        try { this.dest?.disconnect(); } catch { /* ignore */ }
        try { void this.ctx?.close(); } catch { /* ignore */ }
        this.node = null;
        this.dest = null;
        this.ctx = null;
    }
}
